import { api } from './client';
import type { PayoutSlot } from '@/lib/payouts';

export type ActiveTournament = {
  id: number;
  name: string;
  machineName: string;
  entryFeeCents: number;
  maxEntriesPerPlayer: number;
  pullCount: number;
  creditsPerPull: number;
  payoutStructure: PayoutSlot[];
  tiebreakerPulls: number;
  startedAt: string | null;
};

export type PlayerEntry = {
  id: number;
  tournamentId: number;
  userId: number;
  entryNumber: number;
  startingBalance: number | null;
  endingBalance: number | null;
  score: number | null;
  hasStartingPhoto: boolean;
  hasEndingPhoto: boolean;
  isTiebreaker: boolean;
  paid: boolean;
  verifiedByAdmin: boolean;
  submittedAt: string | null;
};

export type RosterRow = {
  userId: number;
  displayName: string;
  entryCount: number;
  allPaid: boolean;
  anySubmitted: boolean;
  allVerified: boolean;
};

/** Admin-only pending verification row. Includes balances + score. */
export type PendingForAdminRow = {
  id: number;
  userId: number;
  userDisplayName: string;
  entryNumber: number;
  entryCountForUser: number;
  startingBalance: number | null;
  endingBalance: number | null;
  score: number | null;
  paid: boolean;
  submittedAt: string | null;
  hasStartingPhoto: boolean;
  hasEndingPhoto: boolean;
};

export type ActiveTournamentResponse = {
  tournament: ActiveTournament | null;
  potCents: number;
  totalEntries: number;
  submittedCount: number;
  verifiedCount: number;
  roster: RosterRow[];
  myEntries: PlayerEntry[];
  canJoin: boolean;
  isApproved: boolean;
  /** Only present when caller is admin AND session is elevated. */
  pendingForAdmin?: PendingForAdminRow[];
};

export const tournamentApi = {
  active: () => api.get<ActiveTournamentResponse>('/api/tournament/active'),
  join: () => api.post<{ entry: PlayerEntry }>('/api/tournament/active/join'),
  submit: (entryId: number) =>
    api.post<{ entry: PlayerEntry }>(`/api/entries/${entryId}/submit`),
};

/**
 * Upload a photo using XHR for upload-progress events (fetch's body-progress
 * API is not yet universally supported on mobile Safari).
 */
export async function uploadEntryPhoto(opts: {
  entryId: number;
  which: 'start' | 'end';
  balance: number;
  file: File;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<PlayerEntry> {
  const form = new FormData();
  form.append('which', opts.which);
  form.append('balance', String(opts.balance));
  form.append('photo', opts.file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/entries/${opts.entryId}/photo`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress(e.loaded, e.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { entry: PlayerEntry };
          resolve(body.entry);
        } catch (err) {
          reject(err);
        }
      } else {
        try {
          const body = JSON.parse(xhr.responseText) as { error?: string; message?: string };
          reject({ status: xhr.status, error: body.error ?? `http_${xhr.status}`, message: body.message });
        } catch {
          reject({ status: xhr.status, error: `http_${xhr.status}` });
        }
      }
    };
    xhr.onerror = () => reject({ status: 0, error: 'network' });
    xhr.send(form);
  });
}

export function photoUrl(entryId: number, which: 'start' | 'end'): string {
  return `/api/photos/${entryId}/${which}`;
}
